// Cricket "My P&L" — the player's profit & loss statement: all-time + 7-day net,
// then one row per match (staked / returned / net). Same visual language as the
// rest of the cricket screens.

import { useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { api } from '@baaligames/shared-ui/lib/api';

type Row = { matchId: string; title: string; shortTitle: string | null; staked: number; returned: number; reward: number; net: number; bets: number; lastAt: string };

const inr = (n: number) => `₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`;
const signed = (n: number) => (n < 0 ? `−${inr(n)}` : `+${inr(n)}`);

export default function Pnl() {
  const [data, setData] = useState<{ totalNet: number; weekNet: number; totalReward: number; matches: Row[] } | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get<any>('/cricket/my-pnl')
      .then((d) => setData(d))
      .catch((e) => setErr(e.message || 'Failed to load'));
  }, []);

  if (err) return <div className="text-center py-10 text-red-500 font-bold">{err}</div>;
  if (!data) return <div className="text-center py-10 text-arena-400 font-bold animate-pulse">Loading P&L…</div>;

  return (
    <div className="space-y-4 max-w-3xl mx-auto w-full">
      {/* totals */}
      <div className="flex gap-3">
        {([['All time', data.totalNet], ['Last 7 days', data.weekNet]] as const).map(([label, n]) => (
          <div key={label} className="flex-1 bg-white dark:bg-arena-800 rounded-2xl border border-arena-200 dark:border-white/5 p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-arena-400 mb-1">{label}</div>
            <div className={`font-mono font-black text-xl ${n < 0 ? 'text-red-500' : n > 0 ? 'text-green-500' : 'text-arena-400'}`}>{signed(n)}</div>
          </div>
        ))}
      </div>

      {/* turnover reward — a house-funded cashback perk on session turnover */}
      {data.totalReward > 0 && (
        <div className="flex items-center gap-3 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-500/10 dark:to-yellow-500/10 rounded-2xl border border-amber-200 dark:border-amber-400/25 p-4">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-400/20 dark:text-amber-300 shrink-0">
            <Icon name="redeem" size={22} filled />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-black text-amber-800 dark:text-amber-200">Turnover reward earned</div>
            <div className="text-[11px] font-bold text-amber-600/80 dark:text-amber-300/70">Cashback on your session turnover — win or lose.</div>
          </div>
          <div className="shrink-0 font-mono font-black text-xl text-amber-600 dark:text-amber-300">{signed(data.totalReward)}</div>
        </div>
      )}

      {/* per-match rows */}
      {data.matches.length === 0 ? (
        <div className="text-center py-10 text-arena-400 font-bold">No settled cricket bets yet.</div>
      ) : (
        <div className="space-y-2">
          {data.matches.map((m) => (
            <div key={m.matchId || 'other'} className="bg-white dark:bg-arena-800 rounded-2xl border border-arena-200 dark:border-white/5 p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-black text-sm text-arena-900 dark:text-white truncate">{m.title || 'Other entries'}</div>
                  <div className="text-[10px] font-bold text-arena-400 flex items-center gap-1 mt-0.5">
                    <Icon name="receipt_long" size={11} />{m.bets} bet{m.bets === 1 ? '' : 's'}
                    {m.lastAt ? <span>· {new Date(m.lastAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span> : null}
                  </div>
                </div>
                <div className={`shrink-0 font-mono font-black text-lg ${m.net < 0 ? 'text-red-500' : m.net > 0 ? 'text-green-500' : 'text-arena-400'}`}>{signed(m.net)}</div>
              </div>
              <div className="flex gap-4 mt-2 pt-2 border-t border-arena-100 dark:border-white/5 text-[11px] font-bold text-arena-500">
                <span>Staked <b className="text-arena-800 dark:text-arena-200 font-mono">{inr(m.staked)}</b></span>
                <span>Returned <b className="text-arena-800 dark:text-arena-200 font-mono">{inr(m.returned)}</b></span>
                {m.reward > 0 && <span className="text-amber-600 dark:text-amber-300">Reward <b className="font-mono">+{inr(m.reward)}</b></span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
